import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { loginUserWithPin } from '../../api/productService';
import './Login.css';
import PinInputElement from "../../components/PinInputElement";
import ViewMessage from "../../components/ViewMessage";

const Login: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<'email' | 'pin'>('email');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setToken = useAuthStore((state) => state.setToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const navigate = useNavigate();

  // Redirect authenticated users to courses
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/courses', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Initialize step from URL params
  useEffect(() => {
    const urlStep = searchParams.get('step');
    if (urlStep === 'pin') {
      setStep('pin');
    } else {
        setStep('email');
    }
  }, [searchParams]);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStep('pin');
    sessionStorage.setItem('email', email);
    setSearchParams({ step: 'pin' });
  };

  const handlePinChange = (index: number, value: string) => {
    if (value.length > 1) value = value[0];
    if (!/^\d*$/.test(value)) return;

    setPin(prev => {
      const newPin = [...prev];
      newPin[index] = value;

      // Check if PIN is complete after this change
      if (value && newPin.every(digit => digit !== '')) {
        // Auto-submit when PIN is complete
        const pinCode = newPin.join('');
        autoSubmitPin(pinCode);
      }

      return newPin;
    });

  };

  const resetPinAndFocus = () => {
    setPin(['', '', '', '']);
    // Focus the first PIN input after a short delay to ensure DOM is updated
    setTimeout(() => {
      const firstInput = document.getElementById('pin-input-0');
      firstInput?.focus();
    }, 100);
  };

  const autoSubmitPin = async (pinCode: string) => {
    if (loading) return; // Prevent multiple simultaneous requests
    setLoading(true);
    setError('');
    try {
      const response = await loginUserWithPin(email, pinCode);
      if (response?.jwt) {
        setToken(response.jwt);
        navigate('/dashboard');
      } else {
        setError('Invalid credentials');
        setTimeout(() => {
          resetPinAndFocus();
        }, 1000)
        setTimeout(() => {
          setError('');
        }, 4000)
      }
    } catch (err) {
      // setError('Login failed. Please try again.');
      setTimeout(() => {
        resetPinAndFocus();
      }, 1000)
      setTimeout(() => {
        setError('');
      }, 4000)
    } finally {
      setLoading(false);
    }
  };


  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pinCode = pin.join('');
    if (pinCode.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await loginUserWithPin(email, pinCode);
      if (response?.jwt) {
        setToken(response.jwt);
        navigate('/dashboard');
      } else {
        setError('Invalid credentials');
        resetPinAndFocus();
      }
    } catch (err) {
      // setError('Login failed. Please try again.');
      resetPinAndFocus();
    } finally {
      setLoading(false);
    }
  };

  // Don't render login form if user is already authenticated
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="login-container">
      <ViewMessage
        title={'Oops!'}
        text={error}
        onClose={() => setError('')}
        show={!!error}
        type={'error'}
      />
      {step === 'email' && (
        <form onSubmit={handleEmailSubmit} className="login-form">
          <h1 className="login-title">Log in</h1>
          <div className="login-subtitle">Let's continue your AI journey.</div>
          <div className="login-email-label">Email address</div>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="login-email-input"
            required
            autoFocus
          />
          <button
            type="submit"
            className="login-submit-btn"
            disabled={!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)}
          >
            LOG IN
          </button>
        </form>
      )}
      {step === 'pin' && (
        <form onSubmit={handlePinSubmit} className="pin-form" autoComplete="off">
          <div>
            <h1 className="pin-title">Welcome back!</h1>
            <div className="pin-email-display">{email}</div>
            <div className="pin-inputs-container">
              <PinInputElement
                stage="confirm"
                values={pin}
                autoFocusFirst
                onChange={(idx, val) => handlePinChange(idx, val)}
                borderType="default"
              />
            </div>
          </div>
         <a href="/restore-pass" className="login-forgot-link">Forgot your PIN code?</a>
        </form>
      )}
    </div>
  );
};

export default Login;
